import { useState, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Row, Col, Form, Card, Table, Pagination, Spinner, Dropdown } from 'react-bootstrap';
import Badge from '../../components/base/Badge.js';
import DatePicker from '../../components/base/DatePicker.js';
import RevealDropdown from '../../components/base/RevealDropdown.js';
import EditExecutionLogModal from '../../components/modals/EditExecutionLogModal.js';
import { useExecutionLogs } from '../../features/execution-logs/hooks/useExecutionLogs.js';
import dayjs from 'dayjs';
import { MACHINES as MACHINE_NAMES } from '../../data/operatorConstants.js';

// Single source of truth — the six-machine list here used to omit dhc600 and
// ath600, so those two lines could never be filtered anywhere in the app.
const MACHINES = MACHINE_NAMES.map(m => m.toLowerCase());

const PHASES = [
  'unwinding',
  'pre_stranding',
  'conductor_isolation',
  'rope',
  'interior_sheat',
  'armouring',
  'exterior_sheat',
  'coiling',
  'rewinding'
];

const PHASE_LABELS = {
  unwinding: 'Unwinding',
  pre_stranding: 'Pre-Stranding',
  conductor_isolation: 'Isolation',
  rope: 'Rope',
  interior_sheat: 'Interior Sheath',
  armouring: 'Armouring',
  exterior_sheat: 'Exterior Sheath',
  coiling: 'Coiling',
  rewinding: 'Rewinding'
};

const phaseBadgeColor = (phase) => {
  const map = {
    unwinding: 'secondary',
    pre_stranding: 'info',
    conductor_isolation: 'primary',
    rope: 'info',
    interior_sheat: 'warning',
    armouring: 'danger',
    exterior_sheat: 'success',
    coiling: 'dark',
    rewinding: 'dark'
  };
  return map[phase] || 'secondary';
};

const formatDuration = (sec) => {
  if (!sec) return '-';
  if (sec < 60) return `${sec}s`;
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
};

const formatLength = (v) => {
  if (v == null) return '-';
  return v >= 1000 ? `${(v / 1000).toFixed(2)} km` : `${v.toFixed(1)} m`;
};

const LIMIT = 20;

const ExecutionHistory = () => {
  const [machineId, setMachineId] = useState('');
  const [phase, setPhase] = useState('');
  const [dateRange, setDateRange] = useState([]);
  const [page, setPage] = useState(1);

  // Edit modal state
  const [editLog, setEditLog] = useState(null);
  const [showEdit, setShowEdit] = useState(false);

  const filters = useMemo(() => {
    const f = { page, limit: LIMIT };
    if (machineId) f.machineId = machineId;
    if (phase) f.phase = phase;
    if (dateRange.length === 2) {
      f.dateFrom = dayjs(dateRange[0]).startOf('day').valueOf();
      f.dateTo = dayjs(dateRange[1]).endOf('day').valueOf();
    }
    return f;
  }, [machineId, phase, dateRange, page]);

  const { data, isLoading, error } = useExecutionLogs(filters);

  const logs = data?.data || [];
  const total = data?.total || 0;
  const pages = data?.pages || 1;

  const handleFilterChange = useCallback(
    (setter) => (e) => {
      setter(e?.target ? e.target.value : e);
      setPage(1);
    },
    []
  );

  const clearFilters = () => {
    setMachineId('');
    setPhase('');
    setDateRange([]);
    setPage(1);
  };

  const openEdit = (log) => {
    setEditLog(log);
    setShowEdit(true);
  };

  const closeEdit = () => {
    setShowEdit(false);
    setEditLog(null);
  };

  // pagination range display: "1 to 20 of 154"
  const from = total === 0 ? 0 : (page - 1) * LIMIT + 1;
  const to = Math.min(page * LIMIT, total);

  return (
    <div className="mb-9 mt-0">
      <div className="d-flex align-items-center justify-content-between mb-4">
        <div>
          <h2 className="mb-1">Execution History</h2>
          <p className="text-700 mb-0">
            Completed production tokens across all machines
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-4">
        <Card.Body className="py-3">
          <Row className="g-3 align-items-end">
            <Col xs={12} sm={6} md={3} lg={2}>
              <Form.Label className="mb-1 fs-9 text-700">Machine</Form.Label>
              <Form.Select
                size="sm"
                value={machineId}
                onChange={handleFilterChange(setMachineId)}
              >
                <option value="">All machines</option>
                {MACHINES.map((m) => (
                  <option key={m} value={m}>
                    {m.toUpperCase()}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={12} sm={6} md={3} lg={2}>
              <Form.Label className="mb-1 fs-9 text-700">Phase</Form.Label>
              <Form.Select
                size="sm"
                value={phase}
                onChange={handleFilterChange(setPhase)}
              >
                <option value="">All phases</option>
                {PHASES.map((p) => (
                  <option key={p} value={p}>
                    {PHASE_LABELS[p] || p}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={12} sm={6} md={4} lg={3}>
              <Form.Label className="mb-1 fs-9 text-700">Date range</Form.Label>
              <DatePicker
                options={{ mode: 'range', dateFormat: 'M j, Y' }}
                value={dateRange}
                onChange={(dates) => {
                  setDateRange(dates);
                  if (dates.length === 2) setPage(1);
                }}
                placeholder="Select date range..."
              />
            </Col>

            <Col xs="auto">
              <button
                className="btn btn-sm btn-link text-decoration-none p-0"
                onClick={clearFilters}
              >
                Clear filters
              </button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Table */}
      <div className="mx-n4 px-4 mx-lg-n6 px-lg-6 bg-body-emphasis border-top border-bottom border-translucent position-relative top-1">
        {isLoading ? (
          <div className="text-center py-6">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : error ? (
          <div className="p-4">
            <p className="text-danger">{String(error?.message || error)}</p>
          </div>
        ) : logs.length === 0 ? (
          <div className="text-center py-6 text-700">
            No execution logs found for the selected filters.
          </div>
        ) : (
          <>
            <Table responsive hover className="fs-9 mb-0">
              <thead>
                <tr>
                  <th className="text-800">Machine</th>
                  <th className="text-800">Job</th>
                  <th className="text-800">Product</th>
                  <th className="text-800">Phase</th>
                  <th className="text-800">Token</th>
                  <th className="text-800 text-end">Planned</th>
                  <th className="text-800 text-end">Produced</th>
                  <th className="text-800">Color</th>
                  <th className="text-800">Drum</th>
                  <th className="text-800 text-end">Duration</th>
                  <th className="text-800">Completed</th>
                  <th className="text-800 text-end" style={{ width: 50 }}></th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log._id}>
                    <td>
                      <Badge bg="primary" variant="phoenix" className="fs-10">
                        {(log.machineId || '').toUpperCase()}
                      </Badge>
                    </td>
                    <td>
                      <Link
                        to={`/apps/project-management/project-details/${log.jobId}`}
                        className="fw-semibold text-primary text-decoration-none"
                      >
                        {log.jobCode || `JOB-${(log.jobId || '').slice(0, 8)}`}
                      </Link>
                    </td>
                    <td className="text-truncate" style={{ maxWidth: 180 }}>
                      {log.productLabel || '-'}
                    </td>
                    <td>
                      <Badge
                        bg={phaseBadgeColor(log.phase)}
                        variant="phoenix"
                        className="fs-10"
                      >
                        {PHASE_LABELS[log.phase] || log.phase}
                      </Badge>
                    </td>
                    <td className="text-body-tertiary fs-10">
                      {log.tokenId || '-'}
                    </td>
                    <td className="text-end fw-semibold">
                      {formatLength(log.plannedLength)}
                    </td>
                    <td className="text-end fw-semibold">
                      <span
                        className={
                          log.producedLength >= log.plannedLength
                            ? 'text-success'
                            : 'text-warning'
                        }
                      >
                        {formatLength(log.producedLength)}
                      </span>
                    </td>
                    <td>
                      {log.color ? (
                        <Badge bg="secondary" variant="phoenix" className="fs-10">
                          {log.color}
                        </Badge>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="fs-10">
                      {log.drum_material
                        ? `${log.drum_material} ${log.drum_diameter ? 'D' + log.drum_diameter : ''}`
                        : '-'}
                    </td>
                    <td className="text-end">
                      {formatDuration(log.durationSec)}
                    </td>
                    <td className="fs-10 text-nowrap">
                      {log.completedAt
                        ? dayjs(log.completedAt).format('DD MMM YY, HH:mm')
                        : '-'}
                    </td>
                    <td className="text-end">
                      <RevealDropdown>
                        <Dropdown.Item onClick={() => openEdit(log)}>
                          Edit
                        </Dropdown.Item>
                        <Dropdown.Item
                          as={Link}
                          to={`/apps/project-management/project-details/${log.jobId}`}
                        >
                          View Job
                        </Dropdown.Item>
                      </RevealDropdown>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            {/* Pagination footer */}
            <div className="d-flex justify-content-between align-items-center px-3 py-3">
              <span className="text-700 fs-9">
                {from} to {to} of {total}
              </span>

              {pages > 1 && (
                <Pagination size="sm" className="mb-0">
                  <Pagination.Prev
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  />
                  {Array.from({ length: Math.min(pages, 7) }, (_, i) => {
                    let pageNum;
                    if (pages <= 7) {
                      pageNum = i + 1;
                    } else if (page <= 4) {
                      pageNum = i + 1;
                    } else if (page >= pages - 3) {
                      pageNum = pages - 6 + i;
                    } else {
                      pageNum = page - 3 + i;
                    }
                    return (
                      <Pagination.Item
                        key={pageNum}
                        active={pageNum === page}
                        onClick={() => setPage(pageNum)}
                      >
                        {pageNum}
                      </Pagination.Item>
                    );
                  })}
                  <Pagination.Next
                    disabled={page >= pages}
                    onClick={() => setPage((p) => Math.min(pages, p + 1))}
                  />
                </Pagination>
              )}
            </div>
          </>
        )}
      </div>

      {/* Edit Modal */}
      <EditExecutionLogModal
        show={showEdit}
        onHide={closeEdit}
        log={editLog}
      />
    </div>
  );
};

export default ExecutionHistory;
